// Lead Attribution System Types
// Based on leads-dashboard-demo analysis and requirements

export interface Lead {
  id: string;
  client_name: string;
  client_email: string;
  client_phone: string;
  requested_service: string;
  street_address: string;
  form_id: string;
  form_type: string;
  created_at: string;
  submission_url?: string;
  message?: string;
  status?: string;
  franchise_hint?: string;
  _utm?: UTMParams;
}

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  utm_id?: string;
  channel_group?: string;
}

export interface JobberRequest {
  id: string;
  jobNumber: string;
  client: {
    id: string;
    name: string;
    email: string;
    phone: string;
    address: {
      street: string;
      city: string;
      province: string;
      postalCode: string;
    };
  };
  title: string;
  requestType: string;
  requestStatus: string;
  createdAt: string;
  status: string;
  franchise: 'hamilton' | 'northyork';
  source: string;
  _backendMatch?: {
    leadId: string;
    matchType: string;
    matchScore: number;
    jobId?: string;
    jobTotal?: number;
  };
}

export interface LeadMatch {
  id: string;
  lead_id?: string;
  jobber_request_id: string;
  jobber_client_id?: string;
  jobber_job_id?: string;
  franchise: 'hamilton' | 'northyork';
  
  // Match details
  match_type: 'automatic' | 'manual' | 'none';
  match_score: number; // 0-100
  match_details: MatchDetails;
  matched_by_user_id?: string;
  
  // Job details (populated when job is created)
  job_status?: string;
  job_total?: number;
  job_created_at?: string;
  job_completed_at?: string;
  conversion_days?: number; // Days from lead to job
  
  // Request details
  request_status?: string;
  request_created_at?: string;
  request_source?: string;
  
  // Metadata
  created_at: string;
  updated_at: string;
  expires_at?: string; // For 3-month expiry of unmatched
  
  // Related data from joins
  leads?: Lead;
}

export interface MatchDetails {
  emailMatch: boolean;
  phoneMatch: boolean;
  nameMatch: boolean;
  addressMatch: boolean;
  backendMatch?: boolean;
  matchType?: string;
  emailScore?: number;
  phoneScore?: number;
  nameScore?: number;
  addressScore?: number;
}

export interface DashboardMetrics {
  summary: {
    totalLeads: number;
    totalRequests: number;
    matchedRequests: number;
    unmatchedRequests: number;
    convertedJobs: number;
    totalRevenue: number;
    matchRate: string;
    conversionRate: string;
    avgConversionDays: string;
  };
  byChannel: Record<string, ChannelMetrics>;
  byFranchise: Record<string, FranchiseMetrics>;
  byLocation: Record<string, LocationMetrics>;
}

export interface ChannelMetrics {
  leads: number;
  requests: number;
  jobs: number;
  revenue: number;
  matchRate?: number;
  conversionRate?: number;
}

export interface FranchiseMetrics {
  leads: number;
  requests: number;
  jobs: number;
  revenue: number;
  matchRate?: number;
  conversionRate?: number;
}

export interface LocationMetrics {
  leads: number;
  requests: number;
  jobs: number;
  postalFsa: string;
  city?: string;
}

export interface SyncState {
  account_id: 'hamilton' | 'northyork';
  entity: 'requests' | 'jobs';
  last_cursor?: string;
  last_sync_at: string;
  records_synced: number;
  status: 'active' | 'error' | 'disabled';
  error_message?: string;
  created_at: string;
  updated_at: string;
}

export interface LeadLocation {
  id: string;
  lead_id?: string;
  jobber_request_id?: string;
  postal_code?: string;
  postal_fsa?: string; // First 3 characters (Forward Sortation Area)
  city?: string;
  province: string;
  full_address?: string;
  address_source: 'lead' | 'jobber' | 'manual';
  created_at: string;
}

export interface FilterState {
  status: 'all' | 'matched' | 'unmatched';
  franchise?: 'hamilton' | 'northyork' | 'all';
  channel?: string;
  postalFsa?: string;
  dateRange?: {
    start: Date;
    end: Date;
  };
  // Enhanced filtering capabilities
  requestStatus?: 'all' | 'converted' | 'upcoming' | 'assessment_completed';
  city?: string;
  province?: string;
  source?: string;
  timeFrame?: 'all' | 'last7days' | 'last30days' | 'last90days' | 'thisyear';
  // Drill-down context
  drillDown?: {
    type: 'franchise' | 'city' | 'timeperiod';
    value: string;
    parentFilters?: Partial<FilterState>;
  };
}

// Scoring constants based on leads-dashboard-demo analysis
export const MATCH_SCORES = {
  EMAIL_MATCH: 40,
  PHONE_MATCH: 30,
  NAME_MATCH: 20,
  ADDRESS_MATCH: 10
} as const;

export const CONFIDENCE_THRESHOLDS = {
  HIGH: 70,
  MEDIUM: 40,
  LOW: 1
} as const;

export type ConfidenceLevel = 'high' | 'medium' | 'low' | 'none';

// Channel grouping for UTM parameters
export const CHANNEL_GROUPS = {
  'google': 'Google Ads',
  'facebook': 'Facebook Ads',
  'bing': 'Bing Ads',
  'organic': 'Organic Search',
  'direct': 'Direct',
  'referral': 'Referral',
  'email': 'Email Marketing',
  'social': 'Social Media'
} as const;

export type ChannelGroup = typeof CHANNEL_GROUPS[keyof typeof CHANNEL_GROUPS] | 'Unknown';
